'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
// Import the new localized responsive TabSliderStyles
import TabSliderStyles from './tabSlider/TabSlider';

export default function ExteriorView({ car }) {
    // Basic validations
    if (!car) return null;

    const carSlug = car.image_path_360;
    const totalFrames = car.amount_360 || 36;
    const path360 = car.file_name_360;
    const LOADING_HEIGHT_CLASS = 'h-[500px]';
    const MAX_VIEWER_SIZE = 1000;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [loadProgress, setLoadProgress] = useState(0);
    const [renderedViewerWidth, setRenderedViewerWidth] = useState(MAX_VIEWER_SIZE);

    const isDragging = useRef(false);
    const startX = useRef(0);
    const startFrame = useRef(0);

    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const imageCache = useRef([]);

    // Extract exterior images for the slider
    const images = car.exterior ? [
        car.exterior.image_1,
        car.exterior.image_2,
        car.exterior.image_3,
        car.exterior.image_4,
        car.exterior.image_5,
        car.exterior.image_6,
    ].filter(Boolean) : [];

    // --- 1. Canvas Frame Repaint Logic ---
    const drawFrame = useCallback((index) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const img = imageCache.current[index];

        if (ctx && img && img.complete) {
            if (canvas.width !== img.naturalWidth) {
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
            }
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }
    }, []);

    // --- 2. Advanced Preloader and GPU Decoder ---
    useEffect(() => {
        if (!carSlug || !path360) {
            setIsLoading(false); // If no 360 data exists, just skip loading it
            return;
        }

        setIsLoading(true);
        setLoadProgress(0);
        let loadedCount = 0;
        const cache = [];

        const urls = Array.from({ length: totalFrames }, (_, i) => {
            const fileName = path360.replace('{index}', i + 1);
            return `https://www.barsavosh.co${carSlug}${fileName}`;
        });

        urls.forEach((url, index) => {
            const img = new Image();

            const handleImageFinish = () => {
                loadedCount++;
                setLoadProgress(Math.round((loadedCount / totalFrames) * 100));

                if (loadedCount === totalFrames) {
                    // Only filter out the explicitly null/failed images
                    const validImages = cache.filter(Boolean);
                    imageCache.current = validImages;
                    setIsLoading(false);
                    setTimeout(() => drawFrame(0), 50);
                }
            };

            img.onload = () => {
                if (img.decode) {
                    img.decode()
                        .then(() => {
                            cache[index] = img;
                            handleImageFinish();
                        })
                        .catch(() => {
                            cache[index] = img;
                            handleImageFinish();
                        });
                } else {
                    cache[index] = img;
                    handleImageFinish();
                }
            };

            img.onerror = () => {
                cache[index] = null;
                handleImageFinish();
            };

            img.src = url;
        });
    }, [carSlug, path360, totalFrames, drawFrame]);

    // --- 3. Responsive Viewer Window Size Evaluator ---
    useEffect(() => {
        const updateViewerSize = () => {
            if (containerRef.current && containerRef.current.parentElement) {
                const parentWidth = containerRef.current.parentElement.offsetWidth;
                setRenderedViewerWidth(Math.min(MAX_VIEWER_SIZE, parentWidth));
            }
        };

        updateViewerSize();
        window.addEventListener('resize', updateViewerSize);
        return () => window.removeEventListener('resize', updateViewerSize);
    }, []);

    useEffect(() => {
        if (!isLoading && imageCache.current.length > 0) {
            drawFrame(currentIndex);
        }
    }, [currentIndex, isLoading, drawFrame]);

    // --- 4. Interactive Dragging Mechanics ---
    const handleStart = (clientX) => {
        if (isLoading || imageCache.current.length === 0) return;
        isDragging.current = true;
        startX.current = clientX;
        startFrame.current = currentIndex;
    };

    const handleMove = useCallback((clientX) => {
        if (!isDragging.current || isLoading || imageCache.current.length === 0) return;

        const deltaX = startX.current - clientX;
        const pixelsPerFrame = 12;
        const frameOffset = Math.floor(deltaX / pixelsPerFrame);

        const actualTotal = imageCache.current.length;
        let nextFrame = (startFrame.current + frameOffset) % actualTotal;
        if (nextFrame < 0) nextFrame += actualTotal;

        if (nextFrame !== currentIndex) {
            setCurrentIndex(nextFrame);
        }
    }, [currentIndex, isLoading]);

    const handleEnd = () => {
        isDragging.current = false;
    };

    useEffect(() => {
        const handleGlobalMouseUp = () => handleEnd();
        window.addEventListener('mouseup', handleGlobalMouseUp);
        window.addEventListener('touchend', handleGlobalMouseUp);

        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('touchend', handleGlobalMouseUp);
        };
    }, []);

    const changeFrameStep = (direction) => {
        if (imageCache.current.length === 0) return;
        const actualTotal = imageCache.current.length;
        setCurrentIndex(prev => {
            if (direction === 'next') return (prev + 1) % actualTotal;
            return prev === 0 ? actualTotal - 1 : prev - 1;
        });
    };

    return (
        <div className="w-full flex flex-col items-center select-none">

            {/* 360 Viewer Section */}
            {(carSlug && path360) ? (
                isLoading ? (
                    <div
                        className={`w-full flex flex-col gap-3 justify-center items-center bg-gray-50 rounded-2xl ${LOADING_HEIGHT_CLASS} border border-gray-100 mb-10`}
                        ref={containerRef}
                    >
                        <div className="w-10 h-10 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin" />
                        <p className="text-xs font-mono font-bold text-gray-500 tracking-wider">در حال بارگذاری نمای ۳۶۰ درجه ({loadProgress}%)</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center w-full mb-10">
                        <div
                            ref={containerRef}
                            className="overflow-hidden cursor-grab active:cursor-grabbing rounded-2xl relative w-full flex items-center justify-center bg-transparent"
                            style={{ width: renderedViewerWidth, maxWidth: '100%' }}

                            onMouseDown={(e) => handleStart(e.clientX)}
                            onMouseMove={(e) => handleMove(e.clientX)}

                            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
                            onTouchMove={(e) => handleMove(e.touches[0].clientX)}
                        >
                            <canvas
                                ref={canvasRef}
                                className="w-full h-auto max-h-[600px] object-contain pointer-events-none drop-shadow-xl"
                                style={{ display: 'block' }}
                            />
                        </div>

                        <div className="flex items-center space-x-4 my-6" dir="rtl">
                            <button
                                onClick={() => changeFrameStep('prev')}
                                className="p-3 border border-gray-200 rounded-full hover:bg-gray-100 transition active:scale-95 shadow-sm"
                                aria-label="Next Frame"
                            >
                                <AiOutlineRight className="w-5 h-5 text-gray-700" />
                            </button>

                            <span className="uppercase text-xs tracking-widest font-black text-gray-500 select-none bg-gray-50 px-6 py-2 rounded-full border border-gray-200 shadow-inner">
                                مشاهده °360
                            </span>

                            <button
                                onClick={() => changeFrameStep('next')}
                                className="p-3 border border-gray-200 rounded-full hover:bg-gray-100 transition active:scale-95 shadow-sm"
                                aria-label="Previous Frame"
                            >
                                <AiOutlineLeft className="w-5 h-5 text-gray-700" />
                            </button>
                        </div>
                    </div>
                )
            ) : (
                <div className="w-full text-center py-10 text-gray-400 border border-dashed border-gray-200 rounded-xl mb-10">
                    نمای ۳۶۰ درجه برای این خودرو ثبت نشده است
                </div>
            )}

            {/*
            
            {images.length > 0 && (
                <div className="w-full">
                    <TabSliderStyles images={images} />
                </div>
            )}
            */}
        </div>
    );
}
